import { Meta, ProgressIndicatorCircle, Spinner, theme } from 'aimo-ui';
import { format, isToday, isTomorrow } from 'date-fns';
import { t } from 'i18next';
import React, { useMemo } from 'react';
import styled from 'styled-components/native';
import { Maybe, SalesOrderStatus } from 'types/generatedSchemaTypes';
import { getRemainingDays } from 'utils/timeUtils';

const ProgressIndicatorWrapper = styled.View`
  margin-left: 10px;
  margin-right: 5px;
`;

const WARNING_DAYS = 3;

type RemainingValidityIndicatorProps = {
  permitStatus: Maybe<SalesOrderStatus> | undefined;
  expirationDate: Maybe<string> | undefined;
  permitId: string;
  isRecurring: boolean;
  isPrepaid?: boolean;
  isWaiting?: boolean;
  predefinedEndDate?: Maybe<string>;
};

const createValidDate = (
  dateString: Maybe<string> | undefined
): Date | null => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getImminentRenewal = (date: Date | null): string | undefined => {
  if (!date) {
    return undefined;
  }
  if (isToday(date)) {
    return t('time.todaySmall');
  }
  if (isTomorrow(date)) {
    return t('time.tomorrowSmall');
  }
  return undefined;
};

const formatDateString = (
  dateString: Maybe<string> | undefined,
  dateFormat: string
): string => {
  const date = createValidDate(dateString);
  if (!date) {
    return '';
  }
  return format(date, `d.M.y ${dateFormat}`);
};

type RemainingValidityParams = {
  permitStatus: Maybe<SalesOrderStatus> | undefined;
  expirationDate: Maybe<string> | undefined;
  expirationTime: Date | null;
  remainingDays: number;
  imminentRenewal: string | undefined;
  isRecurring: boolean;
  isPrepaid: boolean;
  isWaiting: boolean | undefined;
  predefinedEndDate: Maybe<string> | undefined;
  dateFormat: string;
};

const getRemainingValidityText = ({
  permitStatus,
  expirationDate,
  expirationTime,
  remainingDays,
  imminentRenewal,
  isRecurring,
  isPrepaid,
  isWaiting,
  predefinedEndDate,
  dateFormat,
}: RemainingValidityParams): string => {
  if (permitStatus === 'TERMINATING') {
    return t('permit.terminatesAutomatically', {
      value: imminentRenewal ?? t('permit.inDays', { count: remainingDays }),
    });
  }

  if (permitStatus === 'EXPIRED') {
    return t('permit.expired');
  }

  if (permitStatus === 'ACTIVATING') {
    return '';
  }

  if (predefinedEndDate) {
    return t('time.ends', {
      endDate: formatDateString(predefinedEndDate, dateFormat),
    });
  }

  if (isWaiting) {
    return '';
  }

  if (isRecurring && !isPrepaid) {
    return t('permit.renewsAutomatically', {
      value: imminentRenewal ?? t('permit.inDays', { count: remainingDays }),
    });
  }

  return formatDateString(expirationDate, dateFormat);
};

const RemainingValidityIndicator = ({
  permitId,
  permitStatus,
  expirationDate,
  isRecurring,
  isPrepaid = false,
  isWaiting,
  predefinedEndDate,
}: RemainingValidityIndicatorProps) => {
  const expirationTime = createValidDate(expirationDate);
  const remainingDays = expirationTime ? getRemainingDays(expirationTime) : 0;
  const imminentRenewal = getImminentRenewal(expirationTime);
  const dateFormat = isPrepaid ? 'HH:mm' : '';

  const remainingValidity = getRemainingValidityText({
    permitStatus,
    expirationDate,
    expirationTime,
    remainingDays,
    imminentRenewal,
    isRecurring,
    isPrepaid,
    isWaiting,
    predefinedEndDate,
    dateFormat,
  });

  const statusColor = useMemo(() => {
    if (isRecurring) {
      return theme.colors.boost.green;
    }
    if (!isRecurring && remainingDays <= WARNING_DAYS && !isPrepaid) {
      return theme.colors.boost.red;
    }
    return theme.colors.black;
  }, [isRecurring, remainingDays, isPrepaid]);

  if (permitStatus === 'ACTIVATING' || permitStatus === 'UPDATING') {
    return <Spinner size={20} />;
  }

  return (
    <>
      <Meta
        testID={`${isPrepaid ? 'prepaid' : 'permit'}-${permitId}-renewal`}
        color={statusColor}>
        {remainingValidity}
      </Meta>
      {permitStatus !== 'EXPIRED' && isRecurring && !isWaiting && (
        <ProgressIndicatorWrapper>
          <ProgressIndicatorCircle
            color={statusColor}
            totalAmount={30}
            filledAmount={remainingDays}
            small
          />
        </ProgressIndicatorWrapper>
      )}
    </>
  );
};

export default RemainingValidityIndicator;
